/**
 * Finance Portal
 * 
 * For: Jayke (payroll, finance, billing)
 * Focus: Payroll, billing, invoices, financial reports
 * 
 * This is a shell for V1.0 - will be fully built in V1.1
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { 
  DollarSign, FileText, CreditCard, TrendingUp,
  AlertCircle, CheckCircle2, ArrowRight, Clock, Calendar,
  Receipt, PiggyBank, BarChart3, Download
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';

export default function FinancePortal() {
  const { user } = useAuth();
  const firstName = user?.first_name || user?.name?.split(' ')[0] || 'there';
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white px-6 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-2 text-emerald-200 text-sm mb-2">
            <DollarSign className="h-4 w-4" />
            Finance Portal
          </div>
          <h1 className="text-2xl font-bold mb-1">Good morning, {firstName}</h1>
          <p className="text-emerald-100">Your financial overview</p>
        </div>
      </div>
      
      {/* Quick Stats */}
      <div className="max-w-7xl mx-auto px-6 -mt-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Revenue MTD</p>
                  <p className="text-2xl font-bold">$42.5K</p>
                </div>
                <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Payroll Due</p>
                  <p className="text-2xl font-bold">$18.2K</p>
                </div>
                <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <DollarSign className="h-5 w-5 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Outstanding</p>
                  <p className="text-2xl font-bold text-amber-600">$8.4K</p>
                </div>
                <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <Receipt className="h-5 w-5 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Margin</p>
                  <p className="text-2xl font-bold text-green-600">42%</p>
                </div>
                <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                  <PiggyBank className="h-5 w-5 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Action Items */}
          <Card className="shadow-lg border-emerald-100">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5 text-emerald-500" />
                Finance Action Items
              </CardTitle>
              <CardDescription>Tasks requiring your attention</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Payroll Ready</p>
                  <p className="text-xs text-muted-foreground">All timesheets approved</p>
                </div>
                <Link to="/payroll">
                  <Button size="sm" variant="outline">Process</Button>
                </Link>
              </div>
              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                <FileText className="h-5 w-5 text-blue-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium">12 Invoices to Send</p>
                  <p className="text-xs text-muted-foreground">Ready for billing cycle</p>
                </div>
                <Link to="/billing">
                  <Button size="sm" variant="outline">Review</Button>
                </Link>
              </div>
              <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
                <AlertCircle className="h-5 w-5 text-amber-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium">3 Overdue Invoices</p>
                  <p className="text-xs text-muted-foreground">$2,400 outstanding</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Quick Links */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-emerald-500" />
                Quick Access
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-3">
              <Link to="/payroll">
                <div className="p-4 rounded-lg border hover:bg-gray-50 transition-colors">
                  <DollarSign className="h-6 w-6 text-emerald-600 mb-2" />
                  <p className="font-medium text-sm">Payroll</p>
                </div>
              </Link>
              <Link to="/billing">
                <div className="p-4 rounded-lg border hover:bg-gray-50 transition-colors">
                  <Receipt className="h-6 w-6 text-blue-600 mb-2" />
                  <p className="font-medium text-sm">Billing</p>
                </div>
              </Link>
              <Link to="/coming-soon/reports">
                <div className="p-4 rounded-lg border hover:bg-gray-50 transition-colors">
                  <BarChart3 className="h-6 w-6 text-purple-600 mb-2" />
                  <p className="font-medium text-sm">Reports</p>
                </div>
              </Link>
              <Link to="/coming-soon/exports">
                <div className="p-4 rounded-lg border hover:bg-gray-50 transition-colors">
                  <Download className="h-6 w-6 text-gray-600 mb-2" />
                  <p className="font-medium text-sm">Exports</p>
                </div>
              </Link>
            </CardContent>
          </Card>
        </div>
        
        {/* Portal Shell Notice */}
        <Card className="mt-6 border-emerald-200 bg-emerald-50/50">
          <CardContent className="p-6 text-center">
            <Badge className="mb-4">V1.0 Preview</Badge>
            <h3 className="font-semibold text-lg mb-2">Finance Portal</h3>
            <p className="text-muted-foreground max-w-md mx-auto">
              Full finance dashboard with revenue analytics, AR aging, 
              and financial reports coming in V1.1. For now, use the sidebar 
              to access Payroll and Billing features.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
